<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import type {
    Project,
    ProjectsByStatus,
    ProjectStatus,
    CalendarEvent,
    WeekPeriod,
    DisplayFilter,
  } from '../lib/types';
  import { showProjects, showEvents } from '../lib/types';
  import {
    getWeekDisplayRange,
    getWeekStart,
    getWeekEnd,
    isCurrentWeek,
    isFutureWeek,
    isPastWeek,
    getWeekNumber,
    weekKeyToMonthKey,
    getShortMonthWithYear,
    categoryIcons,
    statusAccentColors,
    eventsForWeek,
    isMultiDayEvent,
    eventWeekSpan,
    eventTotalDays,
    parseCalendarDate,
    formatCalendarDate,
    progressStyle,
    progressComplete,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import { setupMaxVisible, MAX_VISIBLE_PRESETS } from '../lib/useMaxVisible';
  import {
    draggedProject,
    draggedEvent,
    openProjectRail,
    openProjectModalForCreate,
    openEventModal,
    openEventModalForEdit,
    openEventsRailForWeek,
    openMonthRailForWeek,
    requestProjectMove,
    requestEventMove,
  } from '../lib/stores/ui';
  import { showProjectCellPopover, hideProjectCellPopover } from '../lib/stores/projectCellPopover';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    visibleWeeks: string[];
    projectsByWeek: Record<string, ProjectsByStatus>;
    events: CalendarEvent[];
    currentWeek: string;
    period: WeekPeriod;
    display?: DisplayFilter | null;
  }

  let {
    visibleWeeks,
    projectsByWeek,
    events,
    currentWeek,
    period = 'month',
    display = null,
  }: Props = $props();

  // Period options
  const periodOptions: { value: WeekPeriod; label: string }[] = [
    { value: '1-week', label: '1 Week' },
    { value: 'month', label: 'Month' },
    { value: '5', label: '5 weeks' },
    { value: '7', label: '7 weeks' },
    { value: 'quarter', label: 'Quarter' },
  ];

  function setPeriod(newPeriod: WeekPeriod) {
    if (newPeriod === period) return;

    const params = new URLSearchParams();
    params.set('view', 'timeline');
    if (newPeriod !== 'month') {
      params.set('range', newPeriod);
    }

    router.get(`/week/${currentWeek}?${params.toString()}`, {}, { preserveScroll: true });
  }

  // Group weeks by month for month headers
  let weeksByMonth = $derived(() => {
    const groups: { monthKey: string; monthName: string; weeks: string[] }[] = [];
    let currentGroup: { monthKey: string; monthName: string; weeks: string[] } | null = null;

    for (const weekKey of visibleWeeks) {
      const monthKey = weekKeyToMonthKey(weekKey);
      const monthName = getShortMonthWithYear(monthKey);

      if (!currentGroup || currentGroup.monthKey !== monthKey) {
        currentGroup = { monthKey, monthName, weeks: [] };
        groups.push(currentGroup);
      }
      currentGroup.weeks.push(weekKey);
    }

    return groups;
  });

  // Get all projects for a week
  function getAllProjects(weekKey: string): Project[] {
    const data = projectsByWeek[weekKey] || { planned: [], active: [], almost: [], done: [] };
    return [...(data.active || []), ...(data.planned || []), ...(data.done || [])];
  }

  // Get events for a specific week
  function getWeekEvents(weekKey: string): CalendarEvent[] {
    const weekStart = getWeekStart(weekKey);
    const weekEnd = getWeekEnd(weekKey);

    return eventsForWeek(events, weekKey);
  }

  // Group events by day within a week
  interface DayGroup {
    dateKey: string;
    label: string;
    events: CalendarEvent[];
  }

  function getEventsGroupedByDay(weekKey: string): DayGroup[] {
    const weekEvents = getWeekEvents(weekKey);
    if (weekEvents.length === 0) return [];

    const dayMap = new Map<string, CalendarEvent[]>();

    for (const event of weekEvents) {
      if (!dayMap.has(event.date)) {
        dayMap.set(event.date, []);
      }
      dayMap.get(event.date)!.push(event);
    }

    // Sort by date and build groups, with events sorted within each day
    const sortedKeys = [...dayMap.keys()].sort();
    return sortedKeys.map((dateKey) => {
      const date = parseCalendarDate(dateKey);
      const dayName = formatCalendarDate(date, { weekday: 'short' });
      const dayNum = date.getUTCDate();
      return {
        dateKey,
        label: `${dayName} ${dayNum}`,
        events: sortEventsForDisplay(dayMap.get(dateKey)!),
      };
    });
  }

  // Get truncated day groups that fit within maxVisible events
  function getTruncatedDayGroups(
    dayGroups: DayGroup[],
    maxEvents: number,
  ): { groups: DayGroup[]; hiddenCount: number; totalCount: number } {
    const totalCount = dayGroups.reduce((sum, g) => sum + g.events.length, 0);
    if (totalCount <= maxEvents) {
      return { groups: dayGroups, hiddenCount: 0, totalCount };
    }

    let remaining = maxEvents;
    const truncatedGroups: DayGroup[] = [];

    for (const group of dayGroups) {
      if (remaining <= 0) break;
      if (group.events.length <= remaining) {
        truncatedGroups.push(group);
        remaining -= group.events.length;
      } else {
        truncatedGroups.push({
          ...group,
          events: group.events.slice(0, remaining),
        });
        remaining = 0;
      }
    }

    return {
      groups: truncatedGroups,
      hiddenCount: totalCount - maxEvents,
      totalCount,
    };
  }

  // Week state
  function getWeekState(weekKey: string): 'past' | 'current' | 'future' {
    if (isCurrentWeek(weekKey)) return 'current';
    if (isPastWeek(weekKey)) return 'past';
    return 'future';
  }

  // Navigate to week focus view
  function navigateToWeek(weekKey: string) {
    router.get(`/week/${weekKey}?view=focus`, {}, { preserveScroll: true });
  }

  // Get the first day (Sunday) of a week for target_date
  function getWeekFirstDate(weekKey: string): string {
    const weekStart = getWeekStart(weekKey);
    return weekStart.toISOString().split('T')[0];
  }

  // Get month key from week key for project creation
  function getWeekMonthKey(weekKey: string): string {
    const weekStart = getWeekStart(weekKey);
    return weekStart.toISOString().slice(0, 7);
  }

  function handleAddProject(weekKey: string) {
    const monthKey = getWeekMonthKey(weekKey);
    const targetDate = getWeekFirstDate(weekKey);
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: monthKey,
      targetDate: targetDate,
      status: 'planned',
    });
  }

  function handleAddEvent(weekKey: string) {
    const date = getWeekFirstDate(weekKey);
    openEventModal(date);
  }

  // Double-click on empty area to add
  function handleProjectsDoubleClick(e: MouseEvent, weekKey: string) {
    const target = e.target as HTMLElement;
    if (
      target.closest('.project-bar') ||
      target.closest('.more-bar') ||
      target.closest('.add-inline-btn')
    )
      return;
    handleAddProject(weekKey);
  }

  function handleEventsDoubleClick(e: MouseEvent, weekKey: string) {
    const target = e.target as HTMLElement;
    if (
      target.closest('.event-bar') ||
      target.closest('.more-bar') ||
      target.closest('.add-inline-btn')
    )
      return;
    handleAddEvent(weekKey);
  }

  // Project cell popover handlers
  function handleMoreBarEnter(e: MouseEvent, weekKey: string, projects: Project[]) {
    showProjectCellPopover(projects, getWeekDisplayRange(weekKey), e.currentTarget as HTMLElement);
  }

  function handleMoreBarLeave() {
    hideProjectCellPopover();
  }

  // Dynamic max visible + responsive column count using ResizeObserver
  let timelineGrid: HTMLElement;
  let maxVisibleProjects = $state<number>(5);
  let maxVisibleEvents = $state<number>(3);
  let projectColumns = $state<number>(2);

  // Minimum column width (px) before dropping to fewer columns
  const MIN_COL_WIDTH = 120;
  let projectMaxVisibleCleanup: (() => void) | null = null;

  function setupProjectMaxVisible(container: HTMLElement, columns: number) {
    if (projectMaxVisibleCleanup) {
      projectMaxVisibleCleanup();
      projectMaxVisibleCleanup = null;
    }
    projectMaxVisibleCleanup = setupMaxVisible(
      container,
      {
        itemHeight: 19.5,
        gap: 2,
        containerPadding: 12,
        columns: columns,
        minVisible: 1,
      },
      (max) => {
        maxVisibleProjects = max;
      },
    );
  }

  onMount(() => {
    const cleanups: (() => void)[] = [];

    // Observe the column containers (not the content divs) so we measure available space
    const projectsColumn = timelineGrid?.querySelector('.week-column') as HTMLElement;
    const eventsColumn = timelineGrid?.querySelector('.events-column') as HTMLElement;

    if (projectsColumn) {
      // Responsive column count based on column width
      const colObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const w = projectsColumn.clientWidth - 12; // subtract padding
          const newCols = Math.max(1, Math.min(2, Math.floor(w / MIN_COL_WIDTH)));
          if (newCols !== projectColumns) {
            projectColumns = newCols;
            setupProjectMaxVisible(projectsColumn, newCols);
          }
        });
      });
      colObserver.observe(projectsColumn);
      cleanups.push(() => colObserver.disconnect());

      setupProjectMaxVisible(projectsColumn, projectColumns);
    }

    if (eventsColumn) {
      cleanups.push(
        setupMaxVisible(
          eventsColumn,
          {
            itemHeight: 20,
            gap: 2,
            containerPadding: 12,
            moreButtonHeight: 18,
            minVisible: 1,
          },
          (max) => {
            maxVisibleEvents = max;
          },
        ),
      );
    }

    return () => {
      cleanups.forEach((fn) => fn());
      if (projectMaxVisibleCleanup) projectMaxVisibleCleanup();
    };
  });

  // Drag and drop for projects
  let draggingProjectId = $state<string | null>(null);
  let projectDropTarget = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: Project) {
    hideProjectTooltip(true);
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/project', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }

  function handleProjectDragOver(e: DragEvent, weekKey: string) {
    e.preventDefault();
    if ($draggedProject) {
      projectDropTarget = weekKey;
    }
  }

  function handleProjectDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      projectDropTarget = null;
    }
  }

  function handleProjectDrop(e: DragEvent, weekKey: string) {
    e.preventDefault();
    projectDropTarget = null;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      requestProjectMove(projectId, $draggedProject, 'week', { weekKey, status: 'planned' });
    }
  }

  // Drag and drop for events
  let draggingEventId = $state<string | null>(null);
  let eventDropTarget = $state<string | null>(null);

  function handleEventDragStart(e: DragEvent, event: CalendarEvent) {
    draggingEventId = event.id;
    draggedEvent.set(event.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/event', String(event.id));
    }
  }

  function handleEventDragEnd() {
    draggingEventId = null;
    draggedEvent.set(null);
  }

  function handleEventDragOver(e: DragEvent, weekKey: string) {
    e.preventDefault();
    if ($draggedEvent) {
      eventDropTarget = weekKey;
    }
  }

  function handleEventDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      eventDropTarget = null;
    }
  }

  function handleEventDrop(e: DragEvent, weekKey: string) {
    e.preventDefault();
    eventDropTarget = null;

    // Only process if an event (not a project) is being dragged
    if (!$draggedEvent) return;

    const eventId =
      e.dataTransfer?.getData('application/event') || e.dataTransfer?.getData('text/plain');
    if (eventId) {
      const eventObj = events.find((candidate) => candidate.id === eventId);
      if (eventObj) {
        requestEventMove(eventId, eventObj, 'week', { weekKey });
      } else {
        router.post(
          `/calendar_events/${eventId}/move_to_week`,
          {
            week_key: weekKey,
          },
          {
            preserveScroll: true,
          },
        );
      }
    }
  }
</script>

<div class="timeline-container">
  <!-- Header with period selector -->
  <div class="timeline-header">
    <span class="header-title">Weekly Timeline</span>
    <div class="period-selector">
      {#each periodOptions as opt}
        <button
          type="button"
          class="period-btn"
          class:active={period === opt.value}
          onclick={() => setPeriod(opt.value)}
        >
          {opt.label}
        </button>
      {/each}
    </div>
  </div>

  <!-- Timeline grid -->
  <div class="timeline-scroll" bind:this={timelineGrid}>
    <div class="timeline-grid">
      <!-- Month headers row -->
      <div class="month-headers">
        {#each weeksByMonth() as group (group.monthKey)}
          <div class="month-header" style="flex: {group.weeks.length}">
            <span class="month-name">{group.monthName}</span>
          </div>
        {/each}
      </div>

      <!-- Week headers row -->
      <div class="week-headers">
        {#each visibleWeeks as weekKey (weekKey)}
          {@const state = getWeekState(weekKey)}
          {@const isCurrent = state === 'current'}

          <button
            type="button"
            class="week-header"
            class:past={state === 'past'}
            class:current={isCurrent}
            class:future={state === 'future'}
            onclick={() => navigateToWeek(weekKey)}
          >
            <span class="week-range">{getWeekDisplayRange(weekKey)}</span>
            <span class="week-badge">W{getWeekNumber(weekKey)}</span>
            {#if isCurrent}
              <span class="current-dot"></span>
            {/if}
          </button>
        {/each}
      </div>

      <!-- Projects row -->
      {#if showProjects(display)}
        <div class="projects-row">
          {#each visibleWeeks as weekKey (weekKey)}
            {@const state = getWeekState(weekKey)}
            {@const projects = getAllProjects(weekKey)}
            {@const visibleProjects = projects.slice(0, maxVisibleProjects)}
            {@const hiddenCount = Math.max(0, projects.length - maxVisibleProjects)}

            <div
              class="week-column"
              class:past={state === 'past'}
              class:current={state === 'current'}
              class:future={state === 'future'}
              class:drop-target={projectDropTarget === weekKey}
              role="group"
              ondragover={(e) => handleProjectDragOver(e, weekKey)}
              ondragleave={handleProjectDragLeave}
              ondrop={(e) => handleProjectDrop(e, weekKey)}
              ondblclick={(e) => handleProjectsDoubleClick(e, weekKey)}
            >
              <div
                class="projects-stack"
                style="grid-template-columns: repeat({projectColumns}, 1fr);"
              >
                {#each visibleProjects as project (project.id)}
                  <button
                    type="button"
                    class="project-bar {statusAccentColors[project.status]}"
                    style={progressStyle(project)}
                    data-progress-complete={progressComplete(project) || undefined}
                    class:dragging={draggingProjectId === project.id}
                    draggable={!$viewport.isMobile}
                    ondragstart={(e) => handleDragStart(e, project)}
                    ondragend={handleDragEnd}
                    onclick={() => {
                      hideProjectTooltip(true);
                      openProjectRail(project.id);
                    }}
                    onmouseenter={(e) =>
                      showProjectTooltip(project, e.currentTarget as HTMLElement)}
                    onmouseleave={() => hideProjectTooltip()}
                    title="{project.title} ({project.status}){project.multiWeek
                      ? ` — Week ${project.weekIndex} of ${project.totalWeeks}`
                      : ''}"
                  >
                    <span class="bar-category">{categoryIcons[project.category]}</span>
                    {#if project.isPriority}
                      <span class="bar-star">★</span>
                    {/if}
                    <span class="bar-title">{project.title}</span>
                    {#if project.multiWeek && project.weekIndex && project.totalWeeks}
                      <span class="bar-span">{project.weekIndex}/{project.totalWeeks}</span>
                    {/if}
                  </button>
                {/each}
                {#if hiddenCount > 0}
                  <span
                    class="more-bar"
                    role="note"
                    onmouseenter={(e) => handleMoreBarEnter(e, weekKey, projects)}
                    onmouseleave={handleMoreBarLeave}
                  >
                    +{hiddenCount} more
                  </span>
                {/if}
              </div>
              <button
                type="button"
                class="add-inline-btn hover-reveal"
                onclick={() => handleAddProject(weekKey)}
                title="Add project"
              >
                +
              </button>
            </div>
          {/each}
        </div>
      {/if}

      <!-- Events row -->
      {#if showEvents(display)}
        <div class="events-row">
          {#each visibleWeeks as weekKey (weekKey)}
            {@const state = getWeekState(weekKey)}
            {@const dayGroups = getEventsGroupedByDay(weekKey)}
            {@const truncated = getTruncatedDayGroups(dayGroups, maxVisibleEvents)}

            <div
              class="events-column"
              class:current={state === 'current'}
              class:drop-target={eventDropTarget === weekKey}
              role="group"
              ondragover={(e) => handleEventDragOver(e, weekKey)}
              ondragleave={handleEventDragLeave}
              ondrop={(e) => handleEventDrop(e, weekKey)}
              ondblclick={(e) => handleEventsDoubleClick(e, weekKey)}
            >
              {#if dayGroups.length > 0}
                <div class="events-area">
                  {#each truncated.groups as group (group.dateKey)}
                    <div class="day-group">
                      <div class="day-group-label">{group.label}</div>
                      {#each group.events as event (event.id)}
                        {@const timeStr = formatEventTimeShort(event)}
                        <button
                          type="button"
                          class="event-bar event-{event.type}"
                          class:dragging={draggingEventId === event.id}
                          draggable={!$viewport.isMobile}
                          ondragstart={(e) => handleEventDragStart(e, event)}
                          ondragend={handleEventDragEnd}
                          onclick={() => openEventModalForEdit(event)}
                          title="{timeStr ? `${timeStr} ` : ''}{event.title} ({event.date})"
                        >
                          {#if timeStr}<span class="event-time">{timeStr}</span>{/if}
                          <span class="event-title">{event.title}</span>
                          {#if isMultiDayEvent(event)}
                            {#if eventWeekSpan(event, weekKey).totalWeeks > 1}
                              <span class="bar-span"
                                >{eventWeekSpan(event, weekKey).weekIndex}/{eventWeekSpan(
                                  event,
                                  weekKey,
                                ).totalWeeks}</span
                              >
                            {:else}
                              <span class="bar-span">{eventTotalDays(event)}d</span>
                            {/if}
                          {/if}
                        </button>
                      {/each}
                    </div>
                  {/each}
                  {#if truncated.hiddenCount > 0}
                    <button
                      type="button"
                      class="more-bar"
                      onclick={() => openEventsRailForWeek(weekKey, getWeekDisplayRange(weekKey))}
                    >
                      +{truncated.hiddenCount} more
                    </button>
                  {/if}
                </div>
              {:else}
                <div class="empty-events">—</div>
              {/if}
              <button
                type="button"
                class="add-inline-btn event-add-btn hover-reveal"
                onclick={() => handleAddEvent(weekKey)}
                title="Add event"
              >
                +
              </button>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .timeline-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.375rem;
    gap: 0;
    overflow: hidden;
    background: #f9fafb;
  }

  /* Header */
  .timeline-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.5rem;
    background: white;
    border-radius: 0.375rem 0.375rem 0 0;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .header-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .period-selector {
    display: flex;
    background: #f9fafb;
    border-radius: 0.25rem;
    padding: 0.125rem;
    border: 1px solid #e5e7eb;
  }

  .period-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .period-btn:hover {
    color: #374151;
    background: white;
  }

  .period-btn.active {
    color: #1d4ed8;
    background: white;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Timeline scroll area - fills remaining space */
  .timeline-scroll {
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
    background: white;
    border-radius: 0 0 0.375rem 0.375rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .timeline-grid {
    display: flex;
    flex-direction: column;
    min-width: 100%;
    height: 100%;
  }

  /* Month headers */
  .month-headers {
    display: flex;
    flex-shrink: 0;
  }

  .month-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    background: #f3f4f6;
    border-right: 1px solid #e5e7eb;
  }

  .month-header:last-child {
    border-right: none;
  }

  .month-name {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  /* Week headers */
  .week-headers {
    display: flex;
    flex-shrink: 0;
    border-bottom: 1px solid #e5e7eb;
  }

  .week-header {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.25rem;
    background: none;
    border: none;
    border-right: 1px solid #f3f4f6;
    cursor: pointer;
    transition: background 0.15s;
    position: relative;
  }

  .week-header:last-child {
    border-right: none;
  }

  .week-header:hover {
    background: #f3f4f6;
  }

  .week-header.past {
    opacity: 0.6;
  }

  .week-header.current {
    background: #eff6ff;
  }

  .week-range {
    font-size: 0.625rem;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
  }

  .week-badge {
    font-size: 0.5rem;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.1875rem;
    white-space: nowrap;
  }

  .week-header.current .week-badge {
    color: #1d4ed8;
    background: #dbeafe;
  }

  .current-dot {
    position: absolute;
    top: 0.125rem;
    right: 0.25rem;
    width: 6px;
    height: 6px;
    background: #3b82f6;
    border-radius: 50%;
  }

  /* Projects row - 1/3 of space */
  .projects-row {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  .week-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    border-right: 1px solid #f3f4f6;
    transition: all 0.15s;
    position: relative;
    overflow: hidden;
  }

  .week-column:last-child {
    border-right: none;
  }

  .week-column.past {
    background: #fafafa;
  }

  .week-column.current {
    background: #eff6ff;
    box-shadow: inset 0 0 0 1px #93c5fd;
  }

  .week-column.future {
    background: #fdf9ff;
  }

  .week-column.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6 !important;
  }

  .projects-stack {
    display: grid;
    /* grid-template-columns set via inline style for responsive column count */
    grid-auto-rows: min-content;
    gap: 0.125rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    align-content: start;
  }

  .project-bar {
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem;
    border-radius: 0.1875rem;
    font-size: 0.5625rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
    min-width: 0;
    flex-shrink: 0;
  }

  .bar-category {
    font-size: 0.5rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .project-bar:hover {
    background: #f3f4f6;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .project-bar.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .bar-star {
    color: #facc15;
    font-size: 0.5rem;
    flex-shrink: 0;
  }

  .bar-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
    min-width: 0;
  }

  .bar-span {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0 0.1875rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  .more-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem 0.25rem;
    font-size: 0.5rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: 1px dashed #9ca3af;
    border-radius: 0.1875rem;
    cursor: default;
    text-align: center;
    transition: all 0.15s;
    flex-shrink: 0;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
  }

  .more-bar:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  /* Inline add button (shown on hover, top-right) */
  .add-inline-btn {
    position: absolute;
    top: 0.125rem;
    right: 0.25rem;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    opacity: 0;
    z-index: 1;
  }

  .week-column:hover > .add-inline-btn {
    opacity: 1;
  }

  .events-column:hover > .add-inline-btn {
    opacity: 1;
  }

  .add-inline-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }

  /* Events row - 2/3 of space */
  .events-row {
    display: flex;
    flex: 2;
    min-height: 0;
    border-top: 1px solid #e5e7eb;
  }

  .events-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    background: #faf5ff;
    border-right: 1px solid #f0e6ff;
    position: relative;
    overflow: hidden;
  }

  .events-column:last-child {
    border-right: none;
  }

  .events-column.current {
    background: #f5f3ff;
    box-shadow: inset 0 0 0 1px #c4b5fd;
  }

  .events-column.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6 !important;
  }

  .events-area {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  /* Day group within events */
  .day-group {
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  .day-group-label {
    font-size: 0.4375rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.125rem 0.125rem 0;
    margin-top: 0.125rem;
  }

  .day-group:first-child .day-group-label {
    margin-top: 0;
  }

  .event-bar {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.125rem 0.25rem;
    background: white;
    border-radius: 0.125rem;
    font-size: 0.5rem;
    position: relative;
    padding-left: 0.375rem;
    border: none;
    cursor: grab;
    text-align: left;
    min-width: 0;
    flex-shrink: 0;
  }

  .event-bar:hover {
    filter: brightness(0.95);
  }

  .event-bar.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .event-bar::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    border-radius: 0.125rem 0 0 0.125rem;
  }

  .event-bar.event-appointment::before {
    background: #60a5fa;
  }
  .event-bar.event-travel::before {
    background: #a78bfa;
  }
  .event-bar.event-event::before {
    background: #4ade80;
  }
  .event-bar.event-deadline::before {
    background: #f87171;
  }

  .event-time {
    color: #6b7280;
    font-weight: 600;
    flex-shrink: 0;
  }

  .event-title {
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .empty-events {
    font-size: 0.5rem;
    color: #d1d5db;
    text-align: center;
    padding: 0.5rem;
  }
</style>
